import { FileText, CheckCircle2, Users, Activity, ExternalLink, Radio, Info } from "lucide-react";
import { entityTrack } from "@/lib/entities";
import { ModuleShell, ModuleDelayed, StatusBadge, UpdatedStamp, INNER_CARD, relTime, fmtDate } from "./shell";

/* 01 — Governance (approved frame). Snapshot SIGNALING scope only —
   never implies binding onchain execution (label is entity-configured). */

const STATE_COLORS: Record<string, string> = { active: "#1fe09c", closed: "#9499ad", pending: "#8b5cff" };

const StatTile = ({ icon: Icon, color, value, label, sub, testid }: any) => (
  <div className={`${INNER_CARD} px-[14px] py-[13px] md:px-[16px] md:py-[15px]`} data-testid={testid}>
    <Icon aria-hidden className="h-[15px] w-[15px]" style={{ color }} />
    <p className="nl-sg text-[21px] leading-[26px] md:text-[24px] md:leading-[30px] font-medium text-[#f6f7fc] mt-[8px]">
      {value}
      {sub && <span className="nl-geist text-[10px] md:text-[11px] font-semibold ml-[7px]" style={{ color }}>{sub}</span>}
    </p>
    <p className="nl-geist text-[10px] md:text-[11px] leading-[14px] text-[#9499ad] mt-[2px]">{label}</p>
  </div>
);

const ShareBar = ({ pct }: { pct: number }) => (
  <div className="flex items-center gap-[8px] min-w-0">
    <span className="nl-sg text-[12px] font-medium text-[#f6f7fc] w-[36px] shrink-0">{pct}%</span>
    <div className="h-[4px] flex-1 min-w-[40px] max-w-[92px] rounded-full bg-white/[0.06] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: "linear-gradient(90deg,#22d3ee,#1fe09c)" }} />
    </div>
  </div>
);

const ProposalRow = ({ p, space, entityId, testid }: any) => {
  const color = STATE_COLORS[p.state] || "#9499ad";
  const open = () => entityTrack("governance_proposal_click", entityId, { title: p.title, state: p.state });
  return (
    <div className="border-t border-[#14161f] first:border-0 py-[11px] md:grid md:grid-cols-[minmax(0,1fr)_84px_150px_150px_92px_26px] md:items-center md:gap-[12px]"
      data-testid={testid}>
      <div className="min-w-0">
        <p className="nl-geist text-[12.5px] md:text-[13px] leading-[17px] font-semibold text-[#f6f7fc] truncate">{p.title}</p>
        <p className="nl-mono text-[8px] leading-[12px] text-[color:var(--nl-text-faint)] mt-[2px]">{space}</p>
      </div>
      <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[6px] mt-[7px] md:mt-0 md:contents">
        <div><StatusBadge label={p.state === "closed" ? "CLOSED" : p.state.toUpperCase()} color={color} /></div>
        <p className="nl-geist text-[11px] md:text-[12px] leading-[15px] text-[#b9b9c7] truncate max-w-[150px]">
          {p.leading ? <><span aria-hidden className="text-[#22d3ee]">◦ </span>{p.leading.choice}</> : "—"}
        </p>
        <div>{p.leading ? <ShareBar pct={p.leading.share_pct} /> : <span className="nl-geist text-[11px] text-[#9499ad]">—</span>}</div>
        <p className="nl-mono text-[9px] leading-[13px] text-[#9499ad] whitespace-nowrap">
          {p.state === "active" ? relTime(p.start) : fmtDate(p.end)}
        </p>
        {p.link ? (
          <a href={p.link} target="_blank" rel="noopener noreferrer" onClick={open}
            aria-label={`Open proposal on Snapshot: ${p.title}`}
            className="inline-flex text-[#9499ad] hover:text-[#22d3ee] focus-visible:text-[#22d3ee] transition-colors">
            <ExternalLink className="h-[13px] w-[13px]" />
          </a>
        ) : <span />}
      </div>
    </div>
  );
};

const TableHead = () => (
  <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_84px_150px_150px_92px_26px] md:gap-[12px] pb-[8px]">
    {["PROPOSAL", "STATUS", "LEADING OPTION", "LEADING SHARE", "UPDATED", ""].map((h, i) => (
      <p key={i} className="nl-geist text-[8px] leading-[12px] font-semibold uppercase tracking-[0.08em] text-[color:var(--nl-text-faint)]">{h}</p>
    ))}
  </div>
);

export const EntityGovernanceModule = ({ overview }: { overview: any }) => {
  const gov = overview.governance;
  const name = overview.identity.name;
  if (!gov) return null;
  if (gov.error)
    return <ModuleDelayed num="1" title="Governance" sub="Snapshot Signaling" testid="entity-governance-delayed"
      message="Governance data is temporarily delayed — the signaling provider could not be reached. Nothing is estimated in its place." />;

  const stats = gov.stats || {};
  const passRate = stats.proposals_total ? Math.round((stats.successful_proposals / stats.proposals_total) * 1000) / 10 : null;
  const active = gov.active || [];
  const recent = (gov.recent || []).filter((p: any) => p.state !== "active").slice(0, 3);
  const spaceUrl = `https://snapshot.org/#/${gov.space}`;
  const entityId = overview.identity.entity_id;

  return (
    <ModuleShell num="1" title="Governance" sub="Snapshot Signaling" testid="entity-governance"
      right={
        <span className="nl-mono inline-flex items-center gap-[7px] text-[8px] md:text-[9px] font-semibold uppercase tracking-[0.08em] text-[#b79aff] border border-[#7a3dff]/[0.4] rounded-[9px] px-[11px] py-[7px]"
          data-testid="governance-scope-pill">
          <Radio aria-hidden className="h-[11px] w-[11px]" /> OFF-CHAIN SIGNALING
        </span>
      }
      footNote={
        <span className="inline-flex items-start gap-[7px]">
          <Info aria-hidden className="h-[12px] w-[12px] shrink-0 mt-[1px] text-[#9499ad]" />
          <span data-testid="governance-disclosure">
            Snapshot proposals shown here represent off-chain governance signaling and may not represent
            binding onchain {gov.onchain_label || `${name} governance`} execution.
          </span>
        </span>
      }
      footRight={
        <>
          <a href={spaceUrl} target="_blank" rel="noopener noreferrer"
            className="nl-mono text-[8px] md:text-[9px] font-semibold uppercase text-[#9499ad] hover:text-[#22d3ee] transition-colors inline-flex items-center gap-[5px]">
            Source: Snapshot <ExternalLink className="h-[10px] w-[10px]" />
          </a>
          <UpdatedStamp iso={gov.fetched_at} testid="governance-updated" />
        </>
      }>
      <p className="sr-only" data-testid="governance-sr-summary">
        {name} Snapshot governance has {active.length} active signaling proposal{active.length === 1 ? "" : "s"}.
        {active[0]?.leading ? ` The leading option on ${active[0].title} currently has ${active[0].leading.share_pct} percent support.` : ""}
        {" "}Lifetime: {stats.proposals_total} proposals, {stats.successful_proposals} passed, {stats.followers?.toLocaleString()} followers.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[10px] md:gap-[14px]">
        <StatTile icon={FileText} color="#8b5cff" value={stats.proposals_total?.toLocaleString() ?? "—"} label="Total Proposals" testid="gov-stat-total" />
        <StatTile icon={CheckCircle2} color="#1fe09c" value={stats.successful_proposals?.toLocaleString() ?? "—"} sub={passRate != null ? `${passRate}%` : undefined} label="Passed Proposals" testid="gov-stat-passed" />
        <StatTile icon={Users} color="#22d3ee" value={stats.followers?.toLocaleString() ?? "—"} label="Followers" testid="gov-stat-followers" />
        <StatTile icon={Activity} color="#b79aff" value={active.length} label="Active Proposals" testid="gov-stat-active" />
      </div>

      <div className={`${INNER_CARD} mt-[16px] md:mt-[20px] px-[14px] md:px-[18px] py-[13px] md:py-[15px]`}>
        <div className="flex items-center justify-between gap-[12px]">
          <p className="nl-geist text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.06em] uppercase text-[#f6f7fc]">
            Active Proposals
            <span aria-hidden className="inline-block h-[6px] w-[6px] rounded-full bg-[#1fe09c] ml-[9px] mr-[5px] align-middle" />
            <span className="text-[#1fe09c]">{active.length}</span>
          </p>
          <a href={spaceUrl} target="_blank" rel="noopener noreferrer" data-testid="governance-view-all-active"
            className="nl-geist text-[10px] md:text-[11px] font-semibold text-[#22d3ee] hover:text-white transition-colors whitespace-nowrap">
            View all active →
          </a>
        </div>
        <div className="mt-[10px]">
          <TableHead />
          {active.length ? active.map((p: any, i: number) => (
            <ProposalRow key={i} p={p} space={gov.space} entityId={entityId} testid={`gov-active-row-${i}`} />
          )) : (
            <p className="nl-geist text-[12px] leading-[17px] text-[#9499ad] py-[10px]" data-testid="gov-no-active">
              No active Snapshot proposals right now.
            </p>
          )}
        </div>
      </div>

      {recent.length > 0 && (
        <div className={`${INNER_CARD} mt-[12px] md:mt-[14px] px-[14px] md:px-[18px] py-[13px] md:py-[15px]`}>
          <div className="flex items-center justify-between gap-[12px]">
            <p className="nl-geist text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.06em] uppercase text-[#f6f7fc]">
              Recent Governance
            </p>
            <a href={spaceUrl} target="_blank" rel="noopener noreferrer" data-testid="governance-view-all"
              className="nl-geist text-[10px] md:text-[11px] font-semibold text-[#22d3ee] hover:text-white transition-colors whitespace-nowrap">
              View all →
            </a>
          </div>
          <div className="mt-[6px]">
            {recent.map((p: any, i: number) => (
              <ProposalRow key={i} p={p} space={gov.space} entityId={entityId} testid={`gov-recent-row-${i}`} />
            ))}
          </div>
        </div>
      )}
    </ModuleShell>
  );
};
